import { mailFilter } from '../strings.locstring.json';
import loc from 'owa-localize';
import getViewFilterDisplay from '../helpers/getViewFilterDisplay';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { observer } from 'mobx-react';
import { lazySelectFilter } from 'owa-mail-filter-actions';
import { ActionSource } from 'owa-mail-store';
import { lazyResetFocus } from 'owa-mail-focus-manager';
import KeyboardCharCodes from 'owa-hotkeys/lib/utils/keyboardCharCodes';
import MailFilterContextMenu from './MailFilterContextMenu';
import { getViewFilterForTable, listViewStore } from 'owa-mail-list-store';
import ViewFilter from 'owa-service/lib/contract/ViewFilter';
import * as React from 'react';
import {
    filterViewStore,
    hideFilterDropDownContextMenu,
    showFilterDropDownContextMenu,
} from 'owa-mail-list-filter-store';
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from 'owa-accessibility';
import { ControlIcons } from 'owa-control-icons';

const classNames = require('classnames');
const styles = require('./MailListFilterMenu.scss');

export interface MailListFilterMenuProps {
    supportedViewFilters: ViewFilter[];
    filterMenuSource: ActionSource;
    tableViewId: string;
    containerClassName?: string;
    isInboxWithPivots?: boolean;
}

@observer
export default class MailListFilterMenu extends React.Component<MailListFilterMenuProps, {}> {
    private container: HTMLElement;

    private onFilterClick = (evt: React.MouseEvent<unknown> | React.KeyboardEvent<HTMLElement>) => {
        evt.stopPropagation();
        const isFilterShowing = filterViewStore.filterContextMenuDisplayState;
        const isAllFilter =
            getViewFilterForTable(listViewStore.tableViews.get(this.props.tableViewId)) == 'All';

        // If user is not in the 'All' filter, clicking on the filter should change to 'All'
        // Otherwise, if the user is in the 'All' filter and the filter menu is showing, we should dismiss it
        // but if the filter menu is closed, we should open it.
        if (!isAllFilter) {
            lazySelectFilter.importAndExecute('All', this.props.filterMenuSource);
        } else if (isFilterShowing) {
            this.dismissContextMenu();
        } else {
            showFilterDropDownContextMenu();
        }
    };

    /**
     * Called when user presses keyboard on the filter dropdown title in listview
     */
    private onKeyDownOnFilter = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (evt.keyCode === KeyboardCharCodes.Enter || evt.keyCode === KeyboardCharCodes.Space) {
            evt.preventDefault();
            this.onFilterClick(evt);
        }
    };

    private dismissContextMenu(): void {
        hideFilterDropDownContextMenu();

        // Reset focus when filter context menu is dismissed
        lazyResetFocus.importAndExecute();
    }

    render() {
        const currentSelectedFilter = getViewFilterForTable(
            listViewStore.tableViews.get(this.props.tableViewId)
        );
        const isAllFilterSelected = currentSelectedFilter == 'All';
        const containerClassNames = classNames(
            this.props.containerClassName,
            this.props.isInboxWithPivots && styles.filterPivots,
            filterViewStore.filterContextMenuDisplayState
                ? styles.filterAllOpened
                : styles.filterAll
        );
        const ariaProps: AriaProperties = {
            role: AriaRoles.button,
        };

        return (
            <div
                ref={ref => (this.container = ref)}
                className={containerClassNames}
                onClick={this.onFilterClick}
                onKeyDown={this.onKeyDownOnFilter}
                {...generateDomPropertiesForAria(ariaProps)}
                tabIndex={0}>
                <div className={styles.filterContents}>
                    {isAllFilterSelected
                        ? loc(mailFilter)
                        : getViewFilterDisplay(currentSelectedFilter)}
                    <Icon
                        className={styles.icon}
                        iconName={
                            isAllFilterSelected ? ControlIcons.ChevronDownMed : ControlIcons.Cancel
                        }
                    />
                </div>
                {filterViewStore.filterContextMenuDisplayState && (
                    <MailFilterContextMenu
                        currentViewFilter={currentSelectedFilter}
                        supportedViewFilters={this.props.supportedViewFilters}
                        dismissContextMenu={this.dismissContextMenu}
                        contextMenuTargetElement={this.container}
                        filterMenuSource={this.props.filterMenuSource}
                        tableViewId={this.props.tableViewId}
                    />
                )}
            </div>
        );
    }
}
